import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Play,
  Pause,
  RefreshCw,
  Eye,
  EyeOff,
  Activity,
  TrendingUp,
  Clock,
  Database,
  AlertCircle
} from 'lucide-react';
import apiService from '../services/apiService';

const AIBackgroundMonitoring = () => {
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [monitoringSources, setMonitoringSources] = useState([]);
  const [redFlagAlerts, setRedFlagAlerts] = useState([]);
  const [changeAlerts, setChangeAlerts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const loadMonitoringData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load monitoring sources
      const sourcesData = await apiService.getBackgroundSources();
      setMonitoringSources(sourcesData.sources);
      
      // Load monitoring status
      const statusData = await apiService.getMonitoringStatus();
      setMonitoringStatus(statusData);
      setIsMonitoring(statusData.active_monitoring > 0);
      
      // Load red flag alerts
      const alertsData = await apiService.getRedFlagAlerts();
      setRedFlagAlerts(alertsData.alerts);
      
      // Load change alerts
      const changesData = await apiService.getChangeAlerts();
      setChangeAlerts(changesData.changes);
      
      // Load analytics
      const analyticsData = await apiService.getAnalytics();
      setAnalytics(analyticsData);
      
    } catch (error) {
      console.error('Error loading monitoring data:', error);
      setError('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonitoringData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(loadMonitoringData, 30000);
    return () => clearInterval(interval);
  }, [loadMonitoringData]);

  const startMonitoring = async () => {
    try {
      const response = await apiService.startMonitoring('demo_user', {
        name: 'Demo User',
        date_of_birth: '1990-01-01',
        ssn_last_four: '1234',
        address: '123 Demo St, Demo City, USA'
      });
      
      if (response.status === 'monitoring_started') {
        setIsMonitoring(true);
        loadMonitoringData();
      }
    } catch (error) {
      console.error('Error starting monitoring:', error);
    }
  };

  const stopMonitoring = async () => {
    try {
      const response = await apiService.stopMonitoring('demo_user');
      
      if (response.status === 'monitoring_stopped') {
        setIsMonitoring(false);
        loadMonitoringData();
      }
    } catch (error) {
      console.error('Error stopping monitoring:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'text-red-400 bg-red-900 bg-opacity-20';
      case 'HIGH':
        return 'text-orange-400 bg-orange-900 bg-opacity-20';
      case 'MEDIUM':
        return 'text-yellow-400 bg-yellow-900 bg-opacity-20';
      case 'LOW':
        return 'text-blue-400 bg-blue-900 bg-opacity-20';
      default:
        return 'text-gray-400 bg-gray-900 bg-opacity-20';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <XCircle className="w-5 h-5" />;
      case 'HIGH':
        return <AlertTriangle className="w-5 h-5" />;
      case 'MEDIUM':
        return <AlertCircle className="w-5 h-5" />;
      case 'LOW':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <Shield className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Error Loading Data</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={loadMonitoringData}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">AI Background Monitoring</h1>
          <p className="text-gray-400 mt-1">
            Real-time monitoring using open source and free background check resources
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showDetails ? 'Hide Details' : 'Show Details'}</span>
          </button>
          <button
            onClick={loadMonitoringData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Monitoring Control */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-lg p-6 border border-gray-700"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Monitoring Control</h2>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isMonitoring ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className={`text-sm font-medium ${isMonitoring ? 'text-green-400' : 'text-red-400'}`}>
              {isMonitoring ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {!isMonitoring ? (
            <button
              onClick={startMonitoring}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Play className="w-5 h-5" />
              <span>Start Monitoring</span>
            </button>
          ) : (
            <button
              onClick={stopMonitoring}
              className="flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Pause className="w-5 h-5" />
              <span>Stop Monitoring</span>
            </button>
          )}
          
          <div className="text-sm text-gray-400">
            {monitoringStatus && (
              <>
                Active Sources: {monitoringStatus.active_monitoring}
                <br />
                Total Checks: {analytics?.total_checks || 0}
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Monitoring Sources */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-lg p-6 border border-gray-700"
      >
        <h2 className="text-xl font-semibold text-white mb-4">Monitoring Sources</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(monitoringSources).map(([sourceName, sourceInfo]) => (
            <div key={sourceName} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-white">{sourceInfo.name}</h3>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${sourceInfo.free ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="text-xs text-gray-400">{sourceInfo.type}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    Every {sourceInfo.monitoring_frequency}s
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    {sourceInfo.red_flags.length} red flags
                  </span>
                </div>
                
                {showDetails && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">Red Flags:</div>
                    <div className="flex flex-wrap gap-1">
                      {sourceInfo.red_flags.map((flag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-600 text-gray-300 text-xs rounded"
                        >
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Analytics Overview */}
      {analytics && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-lg p-6 border border-gray-700"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Analytics Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Activity className="w-8 h-8 text-blue-400" />
                <div>
                  <div className="text-sm text-gray-400">Total Checks</div>
                  <div className="text-2xl font-bold text-white">{analytics.total_checks}</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <div>
                  <div className="text-sm text-gray-400">Red Flags</div>
                  <div className="text-2xl font-bold text-white">{analytics.total_red_flags}</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-8 h-8 text-yellow-400" />
                <div>
                  <div className="text-sm text-gray-400">Changes</div>
                  <div className="text-2xl font-bold text-white">{analytics.total_changes}</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Database className="w-8 h-8 text-green-400" />
                <div>
                  <div className="text-sm text-gray-400">Sources</div>
                  <div className="text-2xl font-bold text-white">{analytics.monitoring_sources.length}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Red Flag Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-lg p-6 border border-gray-700"
      >
        <h2 className="text-xl font-semibold text-white mb-4">Red Flag Alerts</h2>
        
        {redFlagAlerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-gray-400">No red flags detected</p>
          </div>
        ) : (
          <div className="space-y-4">
            {redFlagAlerts.slice(0, 10).map((alert, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {getSeverityIcon(alert.severity)}
                    <span className="font-medium text-white">{alert.flag_type}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
                
                <div className="text-sm text-gray-400">
                  Source: {alert.source_name} | Confidence: {(alert.confidence * 100).toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Change Detection Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-lg p-6 border border-gray-700"
      >
        <h2 className="text-xl font-semibold text-white mb-4">Change Detection Alerts</h2>
        
        {changeAlerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-gray-400">No changes detected</p>
          </div>
        ) : (
          <div className="space-y-4">
            {changeAlerts.slice(0, 10).map((change, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-5 h-5 text-yellow-400" />
                    <span className="font-medium text-white">Change Detected</span>
                    <span className="px-2 py-1 bg-yellow-900 bg-opacity-20 text-yellow-400 text-xs rounded">
                      {change.changes.length} changes
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(change.timestamp).toLocaleString()}
                  </div>
                </div>
                
                <div className="text-sm text-gray-400">
                  User: {change.user_id} | Source: {change.source_name}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        <p>AI Background Monitoring System - Real-time threat detection and change monitoring</p>
        <p className="mt-1">Last updated: {analytics?.last_updated ? new Date(analytics.last_updated).toLocaleString() : 'Unknown'}</p>
      </div>
    </div>
  );
};

export default AIBackgroundMonitoring;
